import { useState } from "react";
import { Loader2, Mail, Phone, MapPin, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { BRAND } from "../lib/brand";
import { usePageMeta } from "../hooks/usePageMeta";
import { PAGE_SEO } from "../lib/seo";

const initialState = { name: "", email: "", phone: "", subject: "", message: "" };

const Contact = () => {
  usePageMeta(PAGE_SEO.contact);
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);
    try {
      await api.post("/contact", form);
      setSuccess(true);
      setForm(initialState);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not send your message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="page-contact" className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
      <div className="text-center mb-16 fade-up">
        <p className="text-xs uppercase tracking-[0.3em] text-[#00b4ff] mb-3">Get in touch</p>
        <h1 className="font-script text-6xl sm:text-7xl neon-gradient-text">Contact Us</h1>
        <p className="text-gray-400 max-w-xl mx-auto mt-4">
          Do you have a question that we haven&apos;t answered? Want to know more about what we do or just want to say hi? Drop a line!
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-10">
        <aside className="lg:col-span-1 glass rounded-3xl p-8 border border-white/10 h-fit">
          <h3 className="font-script text-3xl text-white mb-6">Reach out</h3>
          <ul className="space-y-5 text-sm">
            <li className="flex items-start gap-3 text-gray-300">
              <Mail className="w-5 h-5 text-[#ff1a8c] mt-0.5" />
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="contact-email-button"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#ff1a8c]/60 text-white text-[0.72rem] uppercase tracking-[0.2em] font-semibold hover:bg-[#ff1a8c] hover:shadow-[0_0_18px_rgba(255,26,140,0.55)] transition-all"
              >
                Email Us
              </a>
            </li>
            <li className="flex items-start gap-3 text-gray-300">
              <Phone className="w-5 h-5 text-[#ff1a8c] mt-0.5" />
              <span>{BRAND.phone}</span>
            </li>
            <li className="flex items-start gap-3 text-gray-300">
              <MapPin className="w-5 h-5 text-[#ff1a8c] mt-0.5" />
              <span>{BRAND.address}</span>
            </li>
          </ul>
          <hr className="hr-neon my-8" />
          <p className="text-xs uppercase tracking-[0.25em] text-[#00b4ff] mb-3">Hours</p>
          <ul data-testid="contact-hours-list" className="space-y-1.5 text-sm">
            {BRAND.hoursDetailed.map((row) => (
              <li
                key={row.days}
                className="flex items-center justify-between text-gray-300"
              >
                <span className="font-medium text-white">{row.days}</span>
                <span className={row.time === "Closed" ? "text-[#ff1a8c]" : ""}>{row.time}</span>
              </li>
            ))}
          </ul>
        </aside>

        <form
          data-testid="contact-form"
          onSubmit={handleSubmit}
          className="lg:col-span-2 glass rounded-3xl p-8 lg:p-10 border border-white/10 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.18em] text-gray-400">
              Name
              <input
                data-testid="contact-input-name"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Your name"
                required
                className="neon-input"
              />
            </label>
            <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.18em] text-gray-400">
              Email
              <input
                data-testid="contact-input-email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="you@email.com"
                required
                className="neon-input"
              />
            </label>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.18em] text-gray-400">
              Phone <span className="normal-case text-gray-500">(optional)</span>
              <input
                data-testid="contact-input-phone"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="(555) 555-5555"
                className="neon-input"
              />
            </label>
            <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.18em] text-gray-400">
              Subject
              <input
                data-testid="contact-input-subject"
                name="subject"
                value={form.subject}
                onChange={handleChange}
                placeholder="Custom order, event, etc."
                className="neon-input"
              />
            </label>
          </div>

          <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.18em] text-gray-400">
            Message
            <textarea
              data-testid="contact-input-message"
              name="message"
              value={form.message}
              onChange={handleChange}
              placeholder="Tell us what you're craving…"
              rows={6}
              required
              className="neon-input resize-none"
            />
          </label>

          {error && (
            <p data-testid="contact-error" className="text-sm text-[#ff1a8c]">{error}</p>
          )}
          {success && (
            <div data-testid="contact-success" className="flex items-center gap-2 text-sm text-[#00b4ff]">
              <CheckCircle2 className="w-4 h-4" /> Message received — we'll be in touch soon.
            </div>
          )}

          <div className="pt-2">
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={loading}
              className="neon-btn"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Send Message"}
            </button>
          </div>
        </form>
      </div>
    </main>
  );
};

export default Contact;
